import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { CSVLink } from 'react-csv';
import Sidebar from '../../components/Sidebar/Sidebar';
import Header from '../../components/Header/Header';
import withRoleAccess from '../../hoc/withRoleAccess';
import axios from 'axios';
import './TeacherHours.css';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import {
  Card,
  Table,
  Typography,
  Select,
  Button,
  Input,
  Tag,
  Modal,
  Checkbox,
  Space,
  InputNumber,
  message,
  Tooltip,
  Descriptions,
  Alert
} from 'antd';
import {
  SearchOutlined,
  FileExcelOutlined,
  HomeOutlined,
  VideoCameraOutlined,
  EnvironmentOutlined,
  UserOutlined,
  TeamOutlined,
  QuestionCircleOutlined,
  ExclamationCircleOutlined,
  ReloadOutlined
} from '@ant-design/icons';

const { Title, Text } = Typography;
const { Option } = Select;

const formatDate = (dateString) => {
  const date = new Date(dateString);
  if (isNaN(date.getTime())) {
    return 'Invalid Date';
  }
  const options = { day: '2-digit', month: 'short', year: '2-digit' };
  return new Intl.DateTimeFormat('en-GB', options as any).format(date).toUpperCase();
};

const TeacherDetailsPage = () => {
  const { teacherId } = useParams();
  const { t } = useTranslation();
  const [teacher, setTeacher] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [tempMonth, setTempMonth] = useState(new Date().getMonth() + 1);
  const [tempYear, setTempYear] = useState(new Date().getFullYear());
  const [classes, setClasses] = useState([]);
  const [filteredClasses, setFilteredClasses] = useState([]);
  const [hourlyRates, setHourlyRates] = useState({});
  const [isApproved, setIsApproved] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [withholdingTax, setWithholdingTax] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmData, setConfirmData] = useState<any>({});
  const [teacherRate, setTeacherRate] = useState(null);
  const [classChangeLogs, setClassChangeLogs] = useState([]);
  const [remarks, setRemarks] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedClassIds, setSelectedClassIds] = useState([]);
  
  const API_URL = process.env.REACT_APP_API_URL;

  const fetchTeacher = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/teachers/${teacherId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setTeacher(response.data);
    } catch (error) {
      console.error(t('errorFetchingTeacher'), error);
      message.error(t('errorFetchingTeacher'));
    } finally {
      setLoading(false);
    }
  }, [teacherId, API_URL, t]);

  const fetchClasses = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/classes/teacher/${teacherId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setClasses(response.data);
      filterClasses(response.data, selectedMonth, selectedYear);
    } catch (error) {
      console.error(t('errorFetchingClasses'), error);
      message.error(t('errorFetchingClasses'));
    } finally {
      setLoading(false);
    }
  }, [teacherId, selectedMonth, selectedYear, API_URL, t]);

 const fetchApprovedClasses = useCallback(async () => {
  try {
    const token = localStorage.getItem('token');
    const response = await axios.get(`${API_URL}/teacher-hours/${teacherId}/approved`, {
      headers: {
        Authorization: `Bearer ${token}`
      },
      params: {
        period: `${selectedYear}-${selectedMonth < 10 ? '0' + selectedMonth : selectedMonth}`
      }
    });

    if (response.data.length > 0) {
      setFilteredClasses(response.data);

      const rates = {};
      const remarksMap = {};
      response.data.forEach(({ class_id, hourly_rate, remark }) => {
        rates[class_id] = hourly_rate;
        remarksMap[class_id] = remark || "";  // <-- prefill remarks here
      });

      setHourlyRates(rates);
      setRemarks(remarksMap);   // <-- make sure to set remarks state
    } else {
      setIsApproved(false);
    }
  } catch (error) {
    console.error(t('errorFetchingApprovedClasses'), error);
  }
}, [teacherId, selectedMonth, selectedYear, API_URL, t]);


  const fetchClassChangeLogs = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/class-change/class-change-logs`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setClassChangeLogs(response.data);
    } catch (error) {
      console.error('Error fetching class change logs:', error);
    }
  }, [API_URL]);

  const fetchTeacherRate = useCallback(async () => {
  try {
    const token = localStorage.getItem('token');
    const response = await axios.get(`${API_URL}/teacherrate/${teacherId}`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    setTeacherRate(response.data); // ⬅️ you must define this state
  } catch (error) {
    console.error('Error fetching teacher rate:', error);
  }
}, [teacherId, API_URL]);


  useEffect(() => {
    fetchTeacher();
    fetchClasses();
    fetchApprovedClasses();
    fetchClassChangeLogs();
    fetchTeacherRate();
  }, [fetchTeacher, fetchClasses, fetchApprovedClasses, fetchClassChangeLogs, fetchTeacherRate]);

useEffect(() => {
  if (filteredClasses.length > 0) {
    const eligibleIds = filteredClasses
      .filter(
        (cls) => cls.payout !== 'paid' // Only filter out paid classes
        // Removed: isFullyApproved(cls)
      )
      .map((cls) => cls.classid);

    setSelectedClassIds(eligibleIds);
  }
}, [filteredClasses]);


  const handleRefetch = () => {
  setLoading(true); // optional global spinner
  Promise.all([
    fetchTeacher(),
    fetchClasses(),
    fetchApprovedClasses(),
    fetchClassChangeLogs(),
    fetchTeacherRate()
  ])
    .catch((err) => {
      console.error("Error during refetch:", err);
      message.error(t('failedToRefetch'));
    })
    .finally(() => setLoading(false));
};

useEffect(() => {
  handleRefetch(); // run once on mount
}, []);




  const filterClasses = (classes, month, year) => {
    const filtered = classes.filter(cls => {
      const classDate = new Date(cls.date);
      return classDate.getMonth() + 1 === month && classDate.getFullYear() === year;
    });
    setFilteredClasses(filtered);
  };

  const calculateEndTime = (startTime, duration) => {
  if (!startTime || !duration) return 'N/A';
  
  // Parse start time
  const [startHours, startMinutes] = startTime.split(':').map(Number);
  
  // Parse duration based on format like "2h 30m"
  let durationHours = 0;
  let durationMinutes = 0;
  
  // Check if duration is in "Xh Ym" format
  if (duration.includes('h') || duration.includes('m')) {
    const hoursMatch = duration.match(/(\d+)h/);
    const minutesMatch = duration.match(/(\d+)m/);
    
    durationHours = hoursMatch ? parseInt(hoursMatch[1]) : 0;
    durationMinutes = minutesMatch ? parseInt(minutesMatch[1]) : 0;
  } 
  // If it's in "HH:MM" format
  else if (duration.includes(':')) {
    [durationHours, durationMinutes] = duration.split(':').map(Number);
  }
  
  // Calculate end time
  let endHours = startHours + durationHours;
  let endMinutes = startMinutes + durationMinutes;
  
  if (endMinutes >= 60) {
    endHours += Math.floor(endMinutes / 60);
    endMinutes %= 60;
  }
  
  // Format with leading zeros
  return `${endHours.toString().padStart(2, '0')}:${endMinutes.toString().padStart(2, '0')}`;
};

  const handleMonthChange = (value) => {
    setTempMonth(value);
  };

  const handleYearChange = (value) => {
    setTempYear(value);
  };

  const handleSelect = () => {
    setSelectedMonth(tempMonth);
    setSelectedYear(tempYear);
    filterClasses(classes, tempMonth, tempYear);
  };
  const getPlatformRate = (platform, teacherRate, teacher) => {
      if (!teacherRate) {
        return teacher ? teacher.hoursrate : 0;
      }
      
      switch (platform) {
        case 'onsite':
          return teacherRate.onsite_rate || 0;
        case 'online':
          return teacherRate.online_rate || 0;
        case 'athome':
          return teacherRate.athome_rate || 0;
        default:
          return teacher ? teacher.hoursrate : 0;
      }
    };

    // Rate rule: only count classes approved by both teacher & admin
const isFullyApproved = (cls) => true;


  const handleRemarkChange = (classId, value) => {
    setRemarks((prevRemarks) => ({
      ...prevRemarks,
      [classId]: value,
    }));
  };

  const convertScheduleHourToDecimal = (scheduleHour) => {
    if (!scheduleHour) return 0;
    const [hours, minutes] = scheduleHour.split('h').map(part => part.trim().replace('m', ''));
    const hoursDecimal = parseInt(hours, 10) || 0;
    const minutesDecimal = parseInt(minutes, 10) / 60 || 0;
    return hoursDecimal + minutesDecimal;
  };

  const formatDecimalToHoursMinutes = (decimalHours) => {
    const totalMinutes = Math.round(decimalHours * 60);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours}h ${minutes}m`;
  };

const handleApprove = () => {
  if (selectedClassIds.length === 0) {
    message.warning("Please select at least one class to approve.");
    return;
  }

  // Filter only selected classes (removed approval status checks)
  const approvedClassesData = filteredClasses
    .filter(cls =>
      selectedClassIds.includes(cls.classid) &&
      cls.payout !== 'paid' // Only skip already paid
      // Removed: cls.status === 'approved' && cls.adminapprove === 'adminconfirm'
    )
    .map(cls => {
      const defaultRate = getPlatformRate(cls.platform, teacherRate, teacher);
      const hourlyRate = hourlyRates[cls.classid] || defaultRate;
      const hoursWorked = convertScheduleHourToDecimal(cls.schedule_hour);
      return {
        classId: cls.classid,
        classCode: cls.class_code,
        subjectName: cls.subject_name,
        date: formatDate(cls.date),
        platform: cls.platform,
        hourlyRate,
        hoursWorked,
        totalAmount: hourlyRate * hoursWorked,
        remark: remarks[cls.classid] || '',
      };
    });

  if (approvedClassesData.length === 0) {
    message.warning("Selected classes must be unpaid.");
    return;
  }

  // Calculate only selected total
  const totalAmount = approvedClassesData.reduce(
    (acc, item) => acc + item.totalAmount,
    0
  );

  const totalAmountWithTax = withholdingTax ? totalAmount * 0.97 : totalAmount;

  // Populate modal data
  setConfirmData({
    teacherName: `${teacher.firstname} ${teacher.lastname}`,
    period: `${selectedMonth}/${selectedYear}`,
    totalAmount: formatAmount(totalAmountWithTax),
    approvedClasses: approvedClassesData,
  });

  setShowConfirmModal(true);
};

// 4. Update confirmApproval function
const confirmApproval = async () => {
  try {
    if (selectedClassIds.length === 0) {
      message.warning("Please select at least one class to approve.");
      return;
    }

    const token = localStorage.getItem('token');

    // Only include selected classes (removed approval checks)
    const approvedClassesData = filteredClasses
      .filter(cls =>
        selectedClassIds.includes(cls.classid)
        // Removed: && cls.status === 'approved' && cls.adminapprove === 'adminconfirm'
      )
      .map(cls => {
        const scheduleHourDecimal = convertScheduleHourToDecimal(cls.schedule_hour);
        const defaultRate = getPlatformRate(cls.platform, teacherRate, teacher);
        const hourlyRate = hourlyRates[cls.classid] ?? defaultRate;

        return {
          classId: cls.classid,
          hourlyRate,
          hoursWorked: scheduleHourDecimal.toFixed(2),
          remark: remarks[cls.classid] || '',
        };
      });

    if (approvedClassesData.length === 0) {
      message.warning("Please select classes to approve.");
      return;
    }

    // Recalculate total
    const calculatedTotal = approvedClassesData.reduce(
      (acc, cls) => acc + parseFloat(cls.hourlyRate) * parseFloat(cls.hoursWorked),
      0
    );

    const finalTotal = withholdingTax ? calculatedTotal * 0.97 : calculatedTotal;

    await axios.post(`${API_URL}/teacher-hours/${teacherId}/approve`, {
      period: `${selectedYear}-${selectedMonth < 10 ? '0' + selectedMonth : selectedMonth}`,
      approvedClasses: approvedClassesData,
      totalAmount: finalTotal.toFixed(2),
      withholdingTaxApplied: withholdingTax,
    }, {
      headers: { Authorization: `Bearer ${token}` },
    });

    message.success(t('teacherHoursApprovalSaved'));
    setShowConfirmation(true);
    setShowConfirmModal(false);
    setSelectedClassIds([]); // clear selection
    fetchApprovedClasses();
  } catch (error) {
    console.error(t('errorApprovingHours'), error);
    message.error(t('errorApprovingHours'));
  }
};



const getPlatformDisplay = (platform, t) => {
  switch (platform) {
    case 'onsite':
      return (
        <Tag color="blue" icon={<EnvironmentOutlined />}>
          {t('onsite')}
        </Tag>
      );
    case 'online':
      return (
        <Tag color="green" icon={<VideoCameraOutlined />}>
          {t('online')}
        </Tag>
      );
    case 'athome':
      return (
        <Tag color="orange" icon={<HomeOutlined />}>
          {t('athome')}
        </Tag>
      );
    default:
      return (
        <Tag color="default" icon={<QuestionCircleOutlined />}>
          -
        </Tag>
      );
  }
};

 const handleExportCSV = () => {
  const csvData = filteredClasses.map(cls => {
    const defaultRate = getPlatformRate(cls.platform, teacherRate, teacher);
    const hourlyRate = hourlyRates[cls.classid] || defaultRate;
    const scheduleHourDecimal = convertScheduleHourToDecimal(cls.schedule_hour);
    
    return {
      'Teacher ID': teacher ? teacher.userid : '',
      'Teacher Name': teacher ? `${teacher.firstname} ${teacher.lastname}` : '',
      'Class Code': cls.class_code,
      'Class ID': cls.classid,
      'Subject Name': cls.subject_name,
      'Platform': cls.platform,
      'Date': formatDate(cls.date),
      'Schedule Hour': cls.schedule_hour,
      'Hourly Rate': hourlyRate,
      'Total Hours': scheduleHourDecimal.toFixed(2),
      'Total Amount': formatAmount(scheduleHourDecimal * hourlyRate),
      'Status': cls.status,
      'Payout': cls.payout
    };
  });
  return csvData;
};

const handleRateChange = (classId, value) => {
  setHourlyRates(prev => ({
    ...prev,
    [classId]: value
  }));
};


  const totalScheduleHours = filteredClasses.reduce((acc, cls) => acc + convertScheduleHourToDecimal(cls.schedule_hour), 0);

// Remove the restriction - calculate total for ALL classes, not just fully approved
const totalAmount = filteredClasses.reduce((acc, cls) => {
  const scheduleHourDecimal = convertScheduleHourToDecimal(cls.schedule_hour);
  const defaultRate = getPlatformRate(cls.platform, teacherRate, teacher);

  // Show rate for all classes, not just fully approved ones
  const hourlyRate = hourlyRates[cls.classid] !== undefined 
    ? hourlyRates[cls.classid] 
    : defaultRate;

  return acc + (scheduleHourDecimal * hourlyRate);
}, 0);


  const formatAmount = (amount) => {
    return amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }; 

  // Define columns for Ant Design Table
const columns = [
{
  title: t('classCode'),
  dataIndex: 'class_code',
  key: 'class_code',
  width: 200,
  render: (text) => (
    <div
      style={{
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
        lineHeight: 1.3
      }}
    >
      {text}
    </div>
  ),
},
{
  title: t('classInfo'),
  key: 'classInfo',
  width: 200,
  render: (_, record) => (
    <div style={{ lineHeight: 1.3 }}>
      <div style={{ fontSize: '10px' }}>
        {t('class')}: {record.classid}
      </div>
      <div style={{ color: '#555', whiteSpace: 'normal', wordBreak: 'break-word' }}>
        {record.subject_name || t('noSubject')}
      </div>
    </div>
  )
},
  {
  title: t('session'),
  key: 'session',
  width: 100,
  render: (_, record) => {
    const date = formatDate(record.date);

    const start = record.schedule_time?.slice(0, 5) || '-';

    // Reuse your helper
    const end = calculateEndTime(start, record.schedule_hour);


    return (
      <div style={{ lineHeight: 1.4 }}>
        <div><strong>{date}</strong></div>
        <div>{start} - {end}</div>
      </div>
    );
  },
},
  {
    title: t('hours'),
    dataIndex: 'schedule_hour',
    key: 'schedule_hour',
    width: 90,
  },
  {
    title: t('platform'),
    key: 'platform',
    dataIndex: 'platform',
    width: 120,
    render: (platform) => getPlatformDisplay(platform, t),
  },


  {
    title: t('rate'),
    key: 'rate',
    width: 100,
    render: (_, record) => {
      const defaultRate = getPlatformRate(record.platform, teacherRate, teacher);

      // Show rate for all classes
      const effectiveRate = hourlyRates[record.classid] ?? defaultRate;

      return (
        <InputNumber
          min={0}
          value={hourlyRates[record.classid] ?? getPlatformRate(record.platform, teacherRate, teacher)}
          disabled={record.payout === 'paid'}
          style={{
            width: '100%',
            backgroundColor: record.payout === 'paid' ? '#f5f5f5' : 'white',
            opacity: record.payout === 'paid' ? 0.5 : 1,
          }}
          onChange={(value) => handleRateChange(record.classid, value)}
        />
      );
    },
  },
  {
    title: t('total'),
    key: 'total',
    width: 100,
    render: (_, record) => {
      const scheduleHourDecimal = convertScheduleHourToDecimal(record.schedule_hour);
      const defaultRate = getPlatformRate(record.platform, teacherRate, teacher);

      // Show total for all classes
      const hourlyRate = hourlyRates[record.classid] ?? defaultRate;

      const totalAmount = scheduleHourDecimal * hourlyRate;
      return isNaN(totalAmount) ? 'N/A' : `฿${formatAmount(totalAmount)}`;
    },
  },
  
  // COMMENTED OUT - Check In column
  // {
  //   title: t('Check In'),
  //   dataIndex: 'checkin',
  //   key: 'checkin',
  //   width: 130,
  //   render: (checkin) =>
  //     checkin ? moment(checkin).format('HH:mm') : <i style={{ color: '#ccc' }}>—</i>,
  // },
  // COMMENTED OUT - Check Out column
  // {
  //   title: t('Check Out'),
  //   dataIndex: 'checkout',
  //   key: 'checkout',
  //   width: 130,
  //   render: (checkout) =>
  //     checkout ? moment(checkout).format('HH:mm') : <i style={{ color: '#ccc' }}>—</i>,
  // },
  // COMMENTED OUT - Teacher Confirm column
  // {
  //   title: t('confirmteach'),
  //   key: 'status',
  //   width: 180,
  //   render: (_, record) => (
  //     <Space direction="horizontal" size="small">
  //       <Tag color={record.status === 'approved' ? 'green' : 'orange'}>
  //         {record.status === 'approved' ? t('HOURSAPPROVED') : t('confirmpending')}
  //       </Tag>
  //     </Space>
  //   ),
  // },
  {
    title: t('admin'),
    key: 'adminapprove',
    width: 180,
    render: (_, record) => (
      <Space size="small" align="center">
        <Tag color={record.adminapprove === 'adminconfirm' ? 'green' : 'orange'}>
          {record.adminapprove === 'adminconfirm' ? t('Admin Confirmed') : t('confirmpending')}
        </Tag>
      </Space>
    ),
  },
  {
    title: t('status'),
    key: 'payout',
    width: 100,
    render: (_, record) => (
      <Tag color={record.payout === 'pending' ? 'orange' : 'green'}>
        {record.payout === 'pending' ? t('Pending') : t('Paid')}
      </Tag>
    ),
  },
  {
    title: t('remark'),
    key: 'remark',
    width: 200,
    render: (_, record) => (
      <Input
        placeholder="enterRemark"
        value={remarks[record.classid] || ""}
        onChange={(e) => handleRemarkChange(record.classid, e.target.value)}
      />
    ),
  },
];

const summaryContent = () => (
  <Table.Summary fixed>
    <Table.Summary.Row>
      <Table.Summary.Cell index={0} colSpan={4}>
        <Text strong>{t('total')}</Text>
      </Table.Summary.Cell>

      <Table.Summary.Cell index={4}>
        <Text strong>{formatDecimalToHoursMinutes(totalScheduleHours)}</Text>
      </Table.Summary.Cell>

      <Table.Summary.Cell index={5} colSpan={1}></Table.Summary.Cell>
      <Table.Summary.Cell index={6} colSpan={1}></Table.Summary.Cell>
      <Table.Summary.Cell index={7}>
        <Title level={4} style={{ margin: 0 }}>
          {formatAmount(totalAmount)}
        </Title>
      </Table.Summary.Cell>

      <Table.Summary.Cell index={11} colSpan={4}></Table.Summary.Cell>
    </Table.Summary.Row>
  </Table.Summary>
);


  return (
    <div>
      <Sidebar />
      <Header />
      <div className="teacher-details-container">
        <Card title="Teacher Details" className="teacher-details-card" loading={loading}>
          
          {teacher && (
            <>
              <Card 
                  title={
                    <Space align="center">
                      <UserOutlined style={{ fontSize: '24px', color: '#1890ff' }} />
                      <Title level={3} style={{ margin: 0 }}>
                        {teacher.nickname ? `(${teacher.nickname}) ` : ''}{teacher.firstname} {teacher.lastname}
                      </Title>
                    </Space>
                  }
                  className="teacher-info-card"
                  style={{ marginBottom: '24px' }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', flexWrap: 'wrap', gap: '20px' }}>
                    <div style={{ flex: 1, minWidth: '300px' }}>
                      <Title level={5} style={{ marginBottom: '12px', color: '#595959' }}>
                        {t('hourlyRate')}
                      </Title>
                      {teacherRate ? (
                        <Space direction="vertical" size="small" style={{ width: '100%' }}>
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <Tag color="blue" icon={<EnvironmentOutlined />} style={{ minWidth: '90px' }}>
                              {t('onsite')}
                            </Tag>
                            <Text strong style={{ fontSize: '16px', marginLeft: '12px' }}>
                              ฿{teacherRate.onsite_rate}
                            </Text>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <Tag color="green" icon={<VideoCameraOutlined />} style={{ minWidth: '90px' }}>
                              {t('online')}
                            </Tag>
                            <Text strong style={{ fontSize: '16px', marginLeft: '12px' }}>
                              ฿{teacherRate.online_rate}
                            </Text>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <Tag color="orange" icon={<HomeOutlined />} style={{ minWidth: '90px' }}>
                              {t('athome')}
                            </Tag>
                            <Text strong style={{ fontSize: '16px', marginLeft: '12px' }}>
                              ฿{teacherRate.athome_rate}
                            </Text>
                          </div>
                        </Space>

                      ) : (
                        <Tag color="default" icon={<ExclamationCircleOutlined />}>
                          {t('No rate set')}
                        </Tag>
                      )}
                    </div>
                  </div>
                </Card>
                          
              <div style={{ marginBottom: '20px' }}>
                <Space size="middle">
                  <Select
                    value={tempMonth}
                    onChange={handleMonthChange}
                    style={{ width: 120 }}
                  >
                    {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                      <Option key={month} value={month}>
                        {new Date(0, month - 1).toLocaleString('default', { month: 'long' })}
                      </Option>
                    ))}
                  </Select>
                  
                  <Select
                    value={tempYear}
                    onChange={handleYearChange}
                    style={{ width: 100 }}
                  >
                    {Array.from({ length: 10 }, (_, i) => 2024 + i).map(year => (
                      <Option key={year} value={year}>
                        {year}
                      </Option>
                    ))}
                  </Select>
                    <Button 
                      icon={<ReloadOutlined />} 
                      onClick={handleRefetch} 
                      loading={loading}
                    >
                      {t('Refresh')}
                    </Button>

                  <Button 
                    type="primary" 
                    icon={<SearchOutlined />} 
                    onClick={handleSelect}
                  >
                    {t('Search')}
                  </Button>
                  
                  <Tooltip title={t('exportToCSV')}>
                    <CSVLink
                      data={handleExportCSV()}
                      filename={`approved_hours_${teacherId}_${selectedMonth}_${selectedYear}.csv`}
                    >
                      <Button 
                        icon={<FileExcelOutlined />}
                      >
                        {t('exportToCSV')}
                      </Button>
                    </CSVLink>
                  </Tooltip>
                </Space>
              </div>
              <div style={{ marginTop: '20px', textAlign: 'left' }}>
               <Button
                  danger
                  size="large"
                  onClick={handleApprove}
                  disabled={selectedClassIds.length === 0}
                >
                  {t('Approve Selected Classes')}
                </Button>


              </div>                     
              <Table
                  rowSelection={{
                    selectedRowKeys: selectedClassIds,
                    onChange: (selectedKeys) => setSelectedClassIds(selectedKeys),
                    getCheckboxProps: (record) => ({
                      disabled:
                        record.payout === 'paid',
                    }),
                  }}
                  columns={columns}
                  dataSource={filteredClasses}
                  rowKey="classid"
                  scroll={{ x: 'max-content' }}
                  style={{ overflowX: 'auto', marginTop: '20px' }}
                  pagination={{ pageSize: 200 }}
                  size="middle"
                  bordered
                  summary={summaryContent}
                />

              
              {showConfirmation && (
                <Alert
                  message={t('teacherHoursApprovalSaved')}
                  type="success"
                  showIcon
                  closable
                  onClose={() => setShowConfirmation(false)}
                  style={{ marginTop: '20px' }}
                />
              )}
            </>
          )}
        </Card>
      </div>

      <Modal
        title={<Title level={4}>{t('confirm_payment')}</Title>}
        open={showConfirmModal}
        onCancel={() => setShowConfirmModal(false)}
        footer={[
          <Button key="cancel" onClick={() => setShowConfirmModal(false)}>
            {t('cancel')}
          </Button>,
          <Button key="confirm" type="primary" onClick={confirmApproval}>
            {t('confirm')}
          </Button>
        ]}
      >
        <Descriptions bordered column={1}>
          <Descriptions.Item label={t('teacherName')}>{confirmData.teacherName}</Descriptions.Item>
          <Descriptions.Item label={t('period')}>{confirmData.period}</Descriptions.Item>
          <Descriptions.Item label={t('nettotal')}>{confirmData.totalAmount}</Descriptions.Item>
        </Descriptions>
        
        <div style={{ marginTop: '16px' }}>
          <Checkbox 
            checked={withholdingTax} 
            onChange={() => setWithholdingTax(!withholdingTax)}
          >
            {t('sumWithholdingTax')}
          </Checkbox>
        </div>
      </Modal>
    </div>
  );
};

export default withRoleAccess(TeacherDetailsPage, ['superadmin']);